import { useContext } from "react";
import { DateTime } from "luxon";
import { Avatar } from 'primereact/avatar';
import UtilContext from '../../components/UtilContext';
import { DatabaseContext } from '../../redux/DatabaseStore';

const DEADLINE_HOUR = 22;
const DEADLINE_MINUTE = 0;

// diakritika pre filter
function removeDiacritics(str) {
  return (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function safeParseSendingData(template) {
  if (!template?.sending_data) return null;
  try {
    const data =
      typeof template.sending_data === "string"
        ? JSON.parse(template.sending_data)
        : template.sending_data;
    return data && typeof data === "object" ? data : null;
  } catch (e) {
    console.error("Bad sending_data JSON", e, template?.sending_data);
    return null;
  }
}

function isSameCalendarDay(a, b) {
  if (!a || !b) return false;
  return a.toISODate() === b.toISODate();
}

function adjustForWeekendLuxon(date, strategy = "before") {
  let d = date;
  const mode = ["before", "after"].includes(strategy) ? strategy : "before";

  if (mode === "before") {
    while (d.weekday === 6 || d.weekday === 7) {
      d = d.minus({ days: 1 });
    }
  } else {
    while (d.weekday === 6 || d.weekday === 7) {
      d = d.plus({ days: 1 });
    }
  }
  return d.startOf("day");
}

const WEEKDAY_MAP = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

function resolveSpecialMonthlyDayLuxon(today, key) {
  if (!key) return null;
  const m = key.toLowerCase().match(
    /^(first|last)_(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/
  );
  if (!m) return null;

  const position = m[1];
  const weekday = WEEKDAY_MAP[m[2]];
  if (!weekday) return null;

  let d;
  if (position === "first") {
    d = today.startOf("month");
    while (d.weekday !== weekday) {
      d = d.plus({ days: 1 });
    }
  } else {
    d = today.endOf("month");
    while (d.weekday !== weekday) {
      d = d.minus({ days: 1 });
    }
  }
  return d.startOf("day");
}

/**
 * Rovnaká logika ako v PHP isPlannedForToday(), ale pre konkrétny deň (Luxon DateTime).
 */
function isPlannedForDateLuxon(day, data) {
  const intensity = data?.intensity;

  switch (intensity) {
    case "denne":
    case "viackrat_den":
      return true;

    case "tyzdenne": {
      const days = (data.daysOfWeek || [])
        .map(Number)
        .filter(Boolean); // 1-7
      if (!days.length) return false;
      return days.includes(day.weekday);
    }

    case "mesacne": {
      const candidates = [];

      // 1) daysOfMonth: [15, 28, ...]
      if (Array.isArray(data.daysOfMonth)) {
        for (const d of data.daysOfMonth) {
          const dayNum = Number(d);
          if (!dayNum || dayNum <= 0) continue;
          const daysInMonth = day.daysInMonth;
          if (dayNum > daysInMonth) continue;
          candidates.push(day.set({ day: dayNum }));
        }
      }

      // 2) specialMonthlyDays: ["first_monday", "last_friday", ...]
      if (Array.isArray(data.specialMonthlyDays) && data.specialMonthlyDays.length) {
        for (const key of data.specialMonthlyDays) {
          const special = resolveSpecialMonthlyDayLuxon(day, String(key));
          if (special) candidates.push(special);
        }
      } else if (typeof data.dayMonth === "string" && data.dayMonth) {
        // legacy single string
        const special = resolveSpecialMonthlyDayLuxon(day, data.dayMonth);
        if (special) candidates.push(special);
      }

      if (!candidates.length) return false;

      const workdaysOnly = !!data.workdaysOnly;
      const weekendHandling = (data.weekendHandling || "before").toLowerCase();

      return candidates.some((base) => {
        const matchDate = workdaysOnly
          ? adjustForWeekendLuxon(base, weekendHandling)
          : base;
        return isSameCalendarDay(matchDate, day);
      });
    }

    case "rocne": {
      const workdaysOnly = !!data.workdaysOnly;
      const weekendHandling = (data.weekendHandling || "before").toLowerCase();

      let d = null;

      // prefer dayMonth objekt { day, month }
      if (data.dayMonth && typeof data.dayMonth === "object") {
        const dayNum = Number(data.dayMonth.day || 0);
        const monthNum = Number(data.dayMonth.month || 0);
        if (dayNum > 0 && monthNum > 0) {
          let base = day.set({ month: monthNum }).startOf("month");
          if (dayNum <= base.daysInMonth) {
            base = base.set({ day: dayNum });
            d = base;
          }
        }
      } else {
        // legacy day + month
        const dayNum = Number(data.day || 0);
        const monthNum = Number(data.month || 0);
        if (dayNum > 0 && monthNum > 0) {
          let base = day.set({ month: monthNum }).startOf("month");
          if (dayNum <= base.daysInMonth) {
            base = base.set({ day: dayNum });
            d = base;
          }
        }
      }

      if (!d) return false;

      const matchDate = workdaysOnly
        ? adjustForWeekendLuxon(d, weekendHandling)
        : d;

      return isSameCalendarDay(matchDate, day);
    }

    case "nahodne":
    default:
      return false;
  }
}

const norm = (v) => removeDiacritics(String(v ?? "")).toLowerCase().trim();

const parseDT = (value) => {
  if (!value) return null;
  let dt = DateTime.fromISO(value);
  if (dt.isValid) return dt;
  dt = DateTime.fromSQL(value);
  if (dt.isValid) return dt;
  return null;
};

/** timezone: voliteľné (napr. z Jednotiek); ak je, "teraz" sa berie v tomto pásme. */
export function getExecutionDeadlineState(rowData, deadline, timezone) {
  const statusRaw = rowData?.checklist_status;
  const status = typeof statusRaw === "string" ? statusRaw.trim() : (statusRaw?.name || "").trim();
  const statusKey = norm(status);

  const isClosed = statusKey === "uzavrety";
  const isCreated = statusKey === "vytvoreny";

  const resultRaw =
    rowData?.checklist_result ??
    rowData?.checklist_result_status ??
    rowData?.result;

  const result = typeof resultRaw === "string" ? resultRaw.trim() : (resultRaw?.name || "").trim();
  const isNOK = norm(result) === "nok";

  const executedAtIso =
    rowData?.datum_dokoncenia ||
    rowData?.completed_at ||
    rowData?.user_update_at ||
    null;

  const executedAt = parseDT(executedAtIso);
  const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();

  const isLateCompletion = !!(executedAt && executedAt.toMillis() > deadline.toMillis());
  const isOverdue = !isClosed && now.toMillis() > deadline.toMillis();

  // NOK + Vytvorený => potlačí overdue (ako u teba), ale nie late completion
  const forceBlack = isNOK && isCreated;

  const isRed = isLateCompletion || (!forceBlack && isOverdue);

  return { isRed };
}

/**
 * Hlavný výpočet deadlinu vykonania:
 * - berie rowData (ChecklistRecord)
 * - pozerá na template.sending_data + created_at
 * - nájde najbližší plánovaný deň >= created_at a dá mu čas 22:00
 */
export function getExecutionDeadline(rowData) {
  const sending = safeParseSendingData(rowData?.template);
  if (!sending || !rowData?.created_at) return null;
  if (sending.intensity === "nahodne") return null;

  let day;
  try {
    day = DateTime.fromISO(rowData.created_at).startOf("day");
    if (!day.isValid) return null;
  } catch {
    return null;
  }

  // hľadáme najbližší plánovaný deň dopredu (max cca rok)
  for (let offset = 0; offset < 370; offset++) {
    const candidate = day.plus({ days: offset });
    if (isPlannedForDateLuxon(candidate, sending)) {
      return candidate.set({
        hour: DEADLINE_HOUR,
        minute: DEADLINE_MINUTE,
        second: 0,
        millisecond: 0,
      });
    }
  }

  return null;
}

/**
 * Stav „po termíne“ pre deadline vyjadrenia vedúceho.
 * Deadline = (user_update_at || created_at) + template.manager_due_days.
 * isRed = vyjadrenie po deadlíne alebo ešte nevyjadrené a deadline prebehol; a stav nie je OK.
 * Používa sa v ChecklistStateList (Vyjadrenia vedúcich) pre filter otvorene_po_terminie.
 * timezone: voliteľné (z Jednotiek); ak je, "teraz" sa berie v tomto pásme.
 */
export function getManagerDeadlineState(rowData, timezone) {
  const baseDate = parseDT(rowData?.user_update_at) || parseDT(rowData?.created_at);
  const dueDays = Number(rowData?.template?.manager_due_days ?? 0);
  if (!baseDate || dueDays <= 0) return { isRed: false };

  const deadline = baseDate.plus({ days: dueDays }).endOf("day");
  const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();
  const expressedAt = parseDT(rowData?.manager_update_at);

  const isLateExpression = !!(expressedAt && expressedAt > deadline);
  const isExpiredAndNotExpressed = !expressedAt && now > deadline;
  const statusOk = (rowData?.status ?? "").toString().toLowerCase().trim() === "ok";

  const isRed = (isLateExpression || isExpiredAndNotExpressed) && !statusOk;
  return { isRed };
}

  function getUserColor(username) {
      // Step 1: Convert username to a number
      if(!username)return `#fff`;
      let sumAscii = 0;
      for (let i = 0; i < username.length; i++) {
          sumAscii += username.charCodeAt(i);
      }
      
      // Step 2: Generate RGB values based on the sum
      let r = (sumAscii * 37) % 256;
      let g = (sumAscii * 59) % 256;
      let b = (sumAscii * 83) % 256;
      
      // Step 3: Convert RGB values to hex format
      let color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      return color;
  }

  function getUserInitials(username){
        if(!username)return "";
            const all_names = username?.trim()?.split(' ');
        return all_names.length == 3 ? all_names.reduce((acc, curr, index) => {
            if(index === all_names.length - 2 || index === all_names.length - 1){
                acc = `${acc}${curr.charAt(0).toUpperCase()}`;
            }
            return acc;
        }, '') 
        : all_names.reduce((acc, curr, index) => {
            if(index === 0 || index === all_names.length - 1){
            acc = `${acc}${curr.charAt(0).toUpperCase()}`;
            }
            return acc;
        }, '');
    }

/**
 * Formatter pre Tabulator (ReactTabulator)
 */
export function ExecutionDeadlineFormatter(props) {
  const utilContext = useContext(UtilContext);
  const formatDate = utilContext?.formatDate;
  const getDateFormat = utilContext?.getDateFormat;
  const ctx = useContext(DatabaseContext);
  const state = Array.isArray(ctx) ? ctx[0] : (ctx?.state ?? undefined);
  const dateTimeFmtFallback = (getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy') + ' HH:mm';
  const rowData = props.cell.getRow().getData();
  const deadline = getExecutionDeadline(rowData);
  if (!deadline) return <div>-</div>;

  const timezone = props?.timezone;
  const { isRed } = getExecutionDeadlineState(rowData, deadline, timezone);
  const isSubstitute = rowData?.is_substitute;
  const userAvatar = rowData?.user && (
    <Avatar
      title={rowData?.user?.employee?.name || rowData?.user?.username}
      label={getUserInitials(
        rowData?.user?.employee?.name || rowData?.user?.username
      )}
      shape="circle"
      style={{
        backgroundColor: getUserColor(rowData?.user?.username),
        color: "#ffffff",
        marginRight: "5px",
      }}
    />
  );

  return (
    <div
      className={`flex items-center justify-start w-full h-full ${
        isRed ? "text-red-600 font-semibold" : "text-black"
      }`}
      style={{ lineHeight: 1 }}
    >
      {rowData?.user && (
        <div style={{ lineHeight: 0 }}>
          {isSubstitute ? (
            <span style={{ position: 'relative', display: 'inline-block' }}>
              {userAvatar}
              <span className="p-badge p-badge-info" style={{ position: 'absolute', top: '-2px', right: '-2px', fontSize: '0.75rem', minWidth: '14px', height: '14px', lineHeight: '14px', padding: '0 3px' }}>Z</span>
            </span>
          ) : (
            userAvatar
          )}
        </div>
      )}
      <div>{formatDate ? formatDate(deadline.toISO(), true) : deadline.toFormat(dateTimeFmtFallback)}</div>
    </div>
  );
}

/**
 * Header filter funkcia pre Tabulator.
 * Ak je v headerFilterFuncParams getDateFormat, použije sa pre dátumové varianty.
 */
export function executionDeadlineFilter(headerValue, rowValue, rowData, filterParams) {
  const q = norm(headerValue);
  if (!q) return true;

  const deadline = getExecutionDeadline(rowData);
  if (!deadline) return false;

  const timezone = filterParams?.timezone;
  const { isRed } = getExecutionDeadlineState(rowData, deadline, timezone);

  // kľúčové slová pre červené / nečervené
  if (q === "nok"  || q === "!" || q.includes("cerv") || q.includes("red") || q.includes("po termine") || q.includes("omesk") || q.includes("overdue") || q.includes("late")) {
    return isRed;
  }
  if (q === "ok" || q.includes("zel") || q.includes("green") || q.includes("v poriadku") || q.includes("bez cerv")) {
    return !isRed;
  }

  const dateFmt = (typeof filterParams?.getDateFormat === "function" ? filterParams.getDateFormat() : null) ?? "dd.MM.yyyy";
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const variants = [
    deadline.toFormat(dateFmt),
    deadline.toFormat(dateTimeFmt),
    deadline.toFormat("yyyy-MM-dd"),
    deadline.toFormat("yyyy-MM-dd HH:mm"),
  ];

  return variants.some((v) => norm(v).includes(q));
}

